import { t, Trans } from "@lingui/macro";
import React, { Fragment } from "react";
import { Dispatch } from "redux";
import { RouteComponentProps } from "react-router";
import { connect } from "react-redux";
import {
  selectActiveProject,
  updateSelectedSites,
} from "../../redux/modules/_BETA/selections";
import FilterList from "./components/FilterList";
import { Checkbox, Avatar, ControlGroup } from "@conserveit/blocks";
import Menu from "../../components/Menu";
import Loading from "../../components/Loading";
import { i18n } from "../../i18n";
import MenuFooter from "./components/MenuFooter";
import { SelectedSites } from "./components/SelectedSites";
import Projects from "./components/Projects";
import Sites from "./components/Sites";
import {
  eqSet,
  calcIsAllSelected,
  selectAllSetState,
  projectChangeSetState,
} from "./utils";
import {
  LoadingContainer,
  SearchWrapper,
  Search,
  MenuWrapper,
  ColumnContainer,
  MenuContainer,
  Triangle,
  GlobalSite,
  TitleSite,
  Building,
  Clear,
} from "./SiteMenu.styles";
import { siteSelectEvent } from "../../utils/eventTracking";

interface OwnProps {
  className?: string;
  selectedsites: string[];
  onSitesUpdate(selectedSites: Array<string>): void;
}

interface ConnectedProps {
  activeProject: Insite.AppState["betaSelections"]["activeProject"];
  activeSite: Insite.AppState["betaSelections"]["activeSite"];
  selectedSites: Insite.AppState["betaSelections"]["selectedSites"];
  isMultiSite: Insite.AppState["betaSelections"]["isMultiSite"];
  loggedIn: boolean;
  sites: Insite.AppState["userSites"]["sites"];
  projects: Insite.AppState["userSites"]["projects"];
  projectGroups: Insite.AppState["userSites"]["projectGroups"];
  hasLoaded: Insite.AppState["userSites"]["hasLoaded"];
  isLoading: Insite.AppState["userSites"]["isLoading"];
}

interface DispatchProps {
  selectProject: (projectId: string) => () => void;
  selectSites: (sites: Insite.AppState["userSites"]["sites"]) => void;
}

export interface StateProps {
  shouldFilter: boolean;
  selectedSites: Set<string>;
  matchingProjects: Set<string>;
  matchingSites: Set<string>;
  isAllSelected: boolean;
  isPristine: boolean;
  sitesArray: Insite.Site[];
}

type Props = RouteComponentProps & OwnProps & ConnectedProps & DispatchProps;

function mapStateToProps({ userSites, betaSelections, auth }: Insite.AppState) {
  const { sites, projects, projectGroups, hasLoaded, isLoading } = userSites;
  const { activeProject, activeSite, selectedSites, isMultiSite } =
    betaSelections;

  return {
    activeProject,
    activeSite,
    selectedSites,
    loggedIn: auth.user.token !== undefined,
    isMultiSite,
    sites,
    projects,
    projectGroups,
    hasLoaded,
    isLoading,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    selectProject: (projectId: string) => () =>
      dispatch(selectActiveProject(projectId)),
    selectSites: (sites: Insite.AppState["userSites"]["sites"]) =>
      dispatch(updateSelectedSites(sites)),
  };
}

export class SiteMenu extends React.PureComponent<Props, StateProps> {
  constructor(props: Props) {
    super(props);
    this.state = {
      shouldFilter: false,
      selectedSites: new Set(),
      matchingProjects: new Set(),
      matchingSites: new Set(),
      isAllSelected: false,
      isPristine: true,
      sitesArray: Object.values(props.sites),
    };
  }

  componentDidMount() {
    this.setSelectedSitesToState(new Set(this.props.selectedSites));
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.selectedSites !== this.props.selectedSites) {
      this.setSelectedSitesToState(new Set(this.props.selectedSites));
    }

    if (prevProps.activeProject !== this.props.activeProject) {
      this.handleActiveProjectChange();
    }

    if (prevProps.sites !== this.props.sites) {
      this.setState({
        sitesArray: Object.values(this.props.sites),
      });
    }
  }

  setSelectedSitesToState(
    selectedSites: Set<Insite.AppState["userSites"]["sites"]>
  ) {
    const isAllSelected = calcIsAllSelected(
      this.props.activeProject.sites,
      selectedSites
    );

    this.setState({
      isAllSelected,
      selectedSites,
      isPristine: true,
    });
  }

  handleActiveProjectChange = () => {
    this.setState(projectChangeSetState(this.props.activeProject.sites));
  };

  handleSiteSelect = (site: Insite.Site) => () => {
    this.setState((state: StateProps) => {
      const clone = new Set(state.selectedSites);
      const isSelected = clone.has(site.syncoId);

      if (isSelected) {
        clone.delete(site.syncoId);
      } else {
        clone.add(site.syncoId);
      }

      const sites =
        state.shouldFilter && state.matchingSites.size !== 0
          ? this.props.activeProject.sites.filter((site: Insite.Site) =>
              state.matchingSites.has(site.syncoId)
            )
          : this.props.activeProject.sites;

      const isAllSelected = calcIsAllSelected(sites, clone);
      const currentlyActive = new Set(this.props.selectedSites);
      const isPristine = eqSet(clone, currentlyActive);

      return {
        isAllSelected,
        selectedSites: clone,
        isPristine,
      };
    });
  };

  handleSelectAll = () => {
    this.setState(
      selectAllSetState(
        this.props.activeProject.sites,
        this.props.selectedSites
      )
    );
  };

  handleFilter = (
    { filteredData, searchString }: any /* TODO: Fix the type */
  ) => {
    const matchingProjects: Set<string> = new Set(
      filteredData.map((site: Insite.Site) => site.projectId)
    );
    const matchingSites: Set<string> = new Set(
      filteredData.map((site: Insite.Site) => site.syncoId)
    );

    this.setState({
      shouldFilter: searchString !== "",
      matchingProjects,
      matchingSites,
    });
  };

  handleClearAll = () => {
    this.setState({
      selectedSites: new Set(),
      isAllSelected: false,
    });
  };

  handleConfirmSelectedSites = (cb: () => void) => () => {
    const selected = Array.from(this.state.selectedSites);
    const { sites, projects } = this.props;
    // Track selected sites + projects
    siteSelectEvent(selected, sites, projects);

    // Set selected in state and url
    this.props.selectSites(selected);
    this.props.onSitesUpdate(selected);
    cb();
  };

  handleCancel = (cb: () => void) => () => {
    this.setSelectedSitesToState(new Set(this.props.selectedSites));
    cb();
  };

  render() {
    const {
      sites,
      projectGroups,
      activeProject,
      activeSite,
      selectProject,
      className,
      isLoading,
      hasLoaded,
      isMultiSite,
    } = this.props;

    const {
      selectedSites,
      matchingSites,
      matchingProjects,
      shouldFilter,
      sitesArray,
    } = this.state;

    const dataReady =
      hasLoaded && activeProject && activeProject.sites !== undefined;
    const menuLabel =
      isMultiSite && this.props.selectedSites.length > 1
        ? i18n._(
            t(
              "site_menu.label"
            )`Viewing ${this.props.selectedSites.length} Sites`
          )
        : activeSite.label;

    return (
      <MenuWrapper className={className}>
        <Menu>
          {(
            { isOpen, toggle, close, menuRef }: any /* TODO: Fix the type */
          ) => (
            <div ref={menuRef}>
              {dataReady && (
                <GlobalSite onClick={toggle} data-cy="SiteMenu">
                  <ControlGroup tight>
                    <Avatar as="span" icon={<Building />} />
                    <TitleSite>
                      <span className="ActiveSite-site">{menuLabel}</span>
                    </TitleSite>
                  </ControlGroup>
                  <Triangle />
                </GlobalSite>
              )}
              <MenuContainer isOpen={isOpen}>
                {isLoading && (
                  <LoadingContainer>
                    <Loading />
                  </LoadingContainer>
                )}
                {dataReady && (
                  <Fragment>
                    <FilterList
                      data={sitesArray}
                      filterKey="label"
                      onChange={this.handleFilter}
                    >
                      {(
                        { inputRef, onChange }: any /* TODO: Fix the type */
                      ) => (
                        <SearchWrapper>
                          <Search
                            noClear
                            inputRef={inputRef}
                            onChange={onChange}
                            placeholder={i18n._(
                              t("site_menu.filter_sites")`Filter sites`
                            )}
                          />
                        </SearchWrapper>
                      )}
                    </FilterList>
                    <ColumnContainer>
                      <div className="Column">
                        <div className="MenuSection-header">
                          <h2>
                            <Trans id="site_menu.projects">Projects</Trans>
                          </h2>
                        </div>
                        {projectGroups && (
                          <div className="MenuSection-list">
                            <Projects
                              projects={projectGroups}
                              matches={matchingProjects}
                              onClick={selectProject}
                              active={activeProject}
                              shouldFilter={shouldFilter}
                            />
                          </div>
                        )}
                      </div>
                      <div className="Column">
                        <div className="MenuSection-header">
                          <h2>
                            <Trans id="site_menu.sites">
                              Sites ({activeProject.sites.length})
                            </Trans>
                          </h2>
                          <Checkbox
                            checked={this.state.isAllSelected}
                            onChange={this.handleSelectAll}
                          />
                        </div>
                        {activeProject && (
                          <Sites
                            sites={activeProject.sites}
                            matches={matchingSites}
                            selectedSites={selectedSites}
                            onClick={this.handleSiteSelect}
                            shouldFilter={shouldFilter}
                          />
                        )}
                      </div>
                      <div className="Column">
                        <div className="MenuSection-header">
                          <h2>
                            <Trans id="site_menu.selected">
                              Selected ({selectedSites.size})
                            </Trans>
                          </h2>
                          <Clear
                            thin
                            appearance="plain"
                            onClick={this.handleClearAll}
                          >
                            <Trans id="site_menu.clear_all">Clear all</Trans>
                          </Clear>
                        </div>
                        <SelectedSites
                          sites={sites}
                          selectedSites={selectedSites}
                          handleClick={this.handleSiteSelect}
                        />
                      </div>
                    </ColumnContainer>
                    <MenuFooter
                      isConfirmDisabled={
                        selectedSites.size < 1 || this.state.isPristine
                      }
                      isZeroSites={selectedSites.size < 1}
                      onCancel={this.handleCancel(close)}
                      onConfirm={this.handleConfirmSelectedSites(close)}
                    />
                  </Fragment>
                )}
              </MenuContainer>
            </div>
          )}
        </Menu>
      </MenuWrapper>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(SiteMenu);
